import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography, chat } from '../../theme';
import { useAppContext } from '../../contexts/AppContext';
import { apiService } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import { createTranslator } from '../../i18n';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface CatalogLinkCardProps {
  catalogId: string;
  /** Route params from the CTA URL (itemId, businessId, contactId, phoneNumber, language) for opening catalog-view */
  routeParams?: Record<string, string>;
  bodyText?: string;
  isOutgoing?: boolean;
  /** When true, render as a special elevated card (outside bubble) */
  specialCard?: boolean;
}

export const CatalogLinkCard: React.FC<CatalogLinkCardProps> = ({
  catalogId,
  routeParams = {},
  bodyText,
  isOutgoing = false,
  specialCard = false,
}) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo } = useAppContext();
  const { locale } = useLocale();
  const t = createTranslator(locale);
  const [catalog, setCatalog] = useState<any>(null);
  const [productCount, setProductCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!catalogId) {
        setLoading(false);
        return;
      }
      try {
        const [catRes, statsRes] = await Promise.all([
          apiService.getCatalogueById(catalogId),
          apiService.getCatalogueStatistics(catalogId).catch(() => null),
        ]);
        if (!cancelled && catRes?.data) setCatalog(catRes.data);
        if (!cancelled && statsRes?.data?.productCount != null) setProductCount(statsRes.data.productCount);
        else if (!cancelled && catalogId) {
          const productsRes = await apiService.getProductsByCatalogue(catalogId, { limit: 1, skip: 0 });
          if (!cancelled && productsRes?.data?.pagination?.total != null) setProductCount(productsRes.data.pagination.total);
        }
      } catch (_) {
        if (!cancelled) setCatalog(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [catalogId]);

  const handleViewCatalog = () => {
    // Business user: open in-app catalogue screen (/catalogue/id)
    if (specialCard) {
      navigateTo('catalogue-detail', catalogId);
      return;
    }
    // Fallback (e.g. non-special card): catalog-view with route params
    navigateTo('catalog-view', undefined, {
      ...routeParams,
      catalogId,
    });
  };

  const cardStyles = specialCard ? styles.specialCard : (isOutgoing ? styles.cardOutgoing : styles.cardIncoming);
  const textStyles = specialCard ? styles.specialText : (isOutgoing ? styles.textOutgoing : styles.textIncoming);

  if (loading) {
    return (
      <View style={[styles.card, cardStyles]}>
        {specialCard && (
          <View style={styles.specialHeader}>
            <Ionicons name="layers-outline" size={20} color={colors.primary} />
            <Text style={styles.specialBadge}>{t('card.catalog')}</Text>
          </View>
        )}
        <ActivityIndicator size="small" color={specialCard ? colors.primary : (isOutgoing ? chat.out.accent : colors.primary)} style={{ padding: spacing.lg }} />
      </View>
    );
  }

  const catalogName = catalog?.name || t('card.catalog');
  const countText = productCount != null
    ? (productCount !== 1 ? t('card.catalogProducts', { count: String(productCount) }) : t('card.catalogProduct', { count: String(productCount) }))
    : '';

  return (
    <View style={[styles.card, cardStyles]}>
      {specialCard && (
        <View style={styles.specialHeader}>
          <Ionicons name="layers-outline" size={20} color={colors.primary} />
          <Text style={styles.specialBadge}>Catalog</Text>
        </View>
      )}
      <View style={[styles.header, specialCard && styles.specialContent]}>
        <View style={[styles.iconWrap, specialCard ? styles.specialIconWrap : (isOutgoing ? styles.iconWrapOutgoing : styles.iconWrapIncoming)]}>
          <Ionicons name="layers-outline" size={specialCard ? 36 : 28} color={specialCard ? colors.primary : (isOutgoing ? chat.out.accent : colors.white)} />
        </View>
        <View style={styles.info}>
          <Text style={[styles.title, textStyles, specialCard && styles.specialTitle]} numberOfLines={1}>{catalogName}</Text>
          {countText ? (
            <Text style={[styles.count, textStyles]}>{countText}</Text>
          ) : null}
        </View>
      </View>
      {!specialCard && bodyText ? (
        <Text style={[styles.body, textStyles]} numberOfLines={2}>{bodyText}</Text>
      ) : null}
      <TouchableOpacity
        style={specialCard ? styles.specialPrimaryBtn : [styles.viewBtn, isOutgoing ? styles.viewBtnOutgoing : styles.viewBtnIncoming]}
        onPress={handleViewCatalog}
      >
        <Ionicons name="eye-outline" size={18} color={specialCard ? colors.white : (isOutgoing ? chat.out.accent : colors.white)} />
        <Text style={[styles.viewBtnText, specialCard ? styles.specialPrimaryBtnText : { color: isOutgoing ? chat.out.accent : colors.white }]}>{t('card.viewCatalog')}</Text>
      </TouchableOpacity>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  card: {
    borderRadius: 20,
    padding: 14,
    maxWidth: '92%',
  },
  cardOutgoing: {
    backgroundColor: chat.out.bg,
  },
  cardIncoming: {
    backgroundColor: colors.background.secondary,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  label: {
    fontSize: typography.fontSize.xs,
    opacity: 0.9,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginBottom: spacing.sm,
  },
  iconWrap: {
    width: 48,
    height: 48,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconWrapOutgoing: {
    backgroundColor: chat.out.chip,
  },
  iconWrapIncoming: {
    backgroundColor: colors.primary,
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
  },
  count: {
    fontSize: typography.fontSize.sm,
    opacity: 0.9,
    marginTop: 2,
  },
  body: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.sm,
    opacity: 0.95,
  },
  textOutgoing: {
    color: chat.out.text,
  },
  textIncoming: {
    color: colors.text.primary,
  },
  viewBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  viewBtnOutgoing: {
    backgroundColor: chat.out.chip,
  },
  viewBtnIncoming: {
    backgroundColor: colors.primary,
  },
  viewBtnText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
  },
  // Special card (elevated, outside bubble) — single card, no wrapper
  specialCard: {
    backgroundColor: colors.background.surface,
    borderWidth: 1,
    borderColor: colors.borderMedium,
    borderRadius: 14,
    padding: 0,
    overflow: 'hidden',
    maxWidth: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 4,
  },
  specialHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.secondary,
  },
  specialBadge: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.primary,
  },
  specialText: {
    color: colors.text.primary,
  },
  specialContent: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    marginBottom: spacing.sm,
  },
  specialIconWrap: {
    width: 56,
    height: 56,
    borderRadius: 14,
    backgroundColor: colors.primaryTransparent,
  },
  specialTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.semibold,
  },
  specialPrimaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    marginHorizontal: spacing.md,
    marginBottom: spacing.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    backgroundColor: colors.primary,
  },
  specialPrimaryBtnText: {
    color: colors.white,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
  },
});
